import { useQuery, useMutation } from '@tanstack/react-query';
import { getSchedulerStatus, triggerRollups } from '../api/client';

export default function Settings() {
  const { data: scheduler, isLoading } = useQuery({
    queryKey: ['scheduler'],
    queryFn: getSchedulerStatus,
  });

  const triggerMutation = useMutation({
    mutationFn: triggerRollups,
  });

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Configure Power Move bot</p>
      </div>

      <div className="space-y-6">
        {/* Scheduler Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Rollup Scheduler
          </h2>

          {isLoading ? (
            <p className="text-gray-500">Loading...</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Status
                  </label>
                  <p className="mt-1">
                    {scheduler?.running ? (
                      <span className="text-green-600 font-medium">
                        ✓ Running
                      </span>
                    ) : (
                      <span className="text-red-600 font-medium">
                        ✗ Stopped
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Schedule
                  </label>
                  <p className="mt-1 font-mono text-sm">{scheduler?.schedule}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Timezone
                  </label>
                  <p className="mt-1">{scheduler?.timezone}</p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <button
                  onClick={() => triggerMutation.mutate()}
                  disabled={triggerMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {triggerMutation.isPending
                    ? 'Triggering...'
                    : 'Trigger Rollups Now'}
                </button>
                {triggerMutation.isSuccess && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ Rollup generation started
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Environment Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Environment
          </h2>
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-gray-500">API Base:</span>{' '}
              <code className="bg-gray-100 px-2 py-1 rounded">
                {window.location.origin}/api
              </code>
            </p>
            <p className="text-gray-500 text-xs mt-4">
              Configure environment variables in .env file or Docker Compose.
            </p>
          </div>
        </div>

        {/* Slack Commands Reference */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Slack Commands
          </h2>

          {/* Reporting */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Reporting</h3>
            <div className="space-y-1 text-sm">
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove report</code>
                <span className="text-gray-500">Submit a new report</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove continue &lt;id&gt;</code>
                <span className="text-gray-500">Add follow-up to existing report</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove list</code>
                <span className="text-gray-500">View your reports (last 3 weeks)</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove list workweek=12-2024</code>
                <span className="text-gray-500">View reports from specific week</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove list customer=Acme</code>
                <span className="text-gray-500">View reports for a customer</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove history &lt;id&gt;</code>
                <span className="text-gray-500">View full thread history</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove edit &lt;id&gt;</code>
                <span className="text-gray-500">Edit a report</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove delete &lt;id&gt;</code>
                <span className="text-gray-500">Delete a report</span>
              </div>
            </div>
          </div>

          {/* Stats & Summaries */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Stats & Summaries</h3>
            <div className="space-y-1 text-sm">
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove stats</code>
                <span className="text-gray-500">View your submission statistics</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove summary</code>
                <span className="text-gray-500">Monthly impact summary (previous month)</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove summary month=11-2024</code>
                <span className="text-gray-500">Summary for specific month</span>
              </div>
            </div>
          </div>

          {/* Manager Commands */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Manager Commands</h3>
            <div className="space-y-1 text-sm">
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove summary team</code>
                <span className="text-gray-500">Team summary (shows who reported/not reported)</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove summary team=Dave IC</code>
                <span className="text-gray-500">View specific team member's reports</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove summary team week=2</code>
                <span className="text-gray-500">View team summary for week 2</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove workweek</code>
                <span className="text-gray-500">Show calendar with work week numbers</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove summary customer</code>
                <span className="text-gray-500">Team reports grouped by customer</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove rollup status</code>
                <span className="text-gray-500">Check rollup status and backlog</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove rollup send</code>
                <span className="text-gray-500">Lock & send rollup to your manager</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove rollup send include-backlog</code>
                <span className="text-gray-500">Include missed weeks in rollup</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove note &lt;id&gt; &lt;note&gt;</code>
                <span className="text-gray-500">Add manager note to team member's report</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove flag &lt;id&gt; &lt;question&gt;</code>
                <span className="text-gray-500">Flag report for clarification (sends DM)</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove flags</code>
                <span className="text-gray-500">View pending flags</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove clarify &lt;id&gt;</code>
                <span className="text-gray-500">Resolve clarification request</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove ownership</code>
                <span className="text-gray-500">View your ownership assignments</span>
              </div>
            </div>
          </div>

          {/* Scheduled Reports */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Scheduled Reports (Role-based)</h3>
            <div className="space-y-1 text-sm">
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove summary exec-report</code>
                <span className="text-gray-500">Executive report (product-focused)</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove summary product-report</code>
                <span className="text-gray-500">Product manager report</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove summary rnd-report</code>
                <span className="text-gray-500">R&D lead report</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove summary sales-report</code>
                <span className="text-gray-500">Sales report (customer-focused)</span>
              </div>
            </div>
          </div>

          {/* Admin */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Admin</h3>
            <div className="space-y-1 text-sm">
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove admin add-customer &lt;name&gt;</code>
                <span className="text-gray-500">Add a new customer</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove admin add-product &lt;name&gt;</code>
                <span className="text-gray-500">Add a new product</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove admin list-customers</code>
                <span className="text-gray-500">List all customers</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove admin list-products</code>
                <span className="text-gray-500">List all products</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove admin set-manager @user @mgr</code>
                <span className="text-gray-500">Set reporting relationship</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove admin remove-manager @user</code>
                <span className="text-gray-500">Remove manager assignment</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove admin show-team @manager</code>
                <span className="text-gray-500">Show team members</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove admin assign-ownership @user customer|product &lt;name&gt;</code>
                <span className="text-gray-500">Assign ownership</span>
              </div>
              <div className="flex">
                <code className="w-64 text-primary-600">/powermove admin remove-ownership @user customer|product &lt;name&gt;</code>
                <span className="text-gray-500">Remove ownership</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
